import { useEffect, useState } from "react";

import useSingleLead from "../../../data/useSingleLead";
import { ICalculation, ICalculationData, ILead, QuoteType } from "../../../models/Lead";
import Card from "../../shared/Card/Card";
import LoadingWheel from "../../shared/LoadingWheel";
import PriceFormat from "../../../utils/priceFormat";

type ApiCalculation = {
  Name?: string;
  IsFixed?: boolean;
  Value?: number;
  BaseValue?: number;
  IsAddition?: boolean;
  CustomCalculation?: string;
  DropOff?: boolean;
};

export const GetQuoteType = (quoteType: QuoteType) => {
  switch (quoteType) {
    case QuoteType.Salvage:
      return "Salvage";
    case QuoteType.ScrapExport:
      return "Scrap Export";
    case QuoteType.SmartPrice:
      return "Smart Price";
  }
};

export default function QuoteCalculationCard({ leadId, quoteType, isCollection, total }: { leadId: string, quoteType: QuoteType, isCollection: boolean, total: number }) {
  const { lead: singleLead } = useSingleLead(leadId, "QuoteCalculationCard");
  const [lead, setLead] = useState<ILead | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [calculations, setCalculations] = useState<ICalculation[]>();
  const [dropOff, setDropOff] = useState<number>();

  const CalculationStatusRow = ({ calculation }: { calculation: ICalculation }) => {
    if (!calculation) return null;
    let { name, isFixed, value, baseValue, isAddition, customCalculation } = calculation;

    let output = "";

    if (value == null && !customCalculation) return null;

    if (customCalculation) {
      output = customCalculation;
    } else if (!isFixed) {
      output = `${value}% of ${PriceFormat(baseValue)} (${PriceFormat(baseValue * value / 100)})`;
    } else {
      output = PriceFormat(value);
    }

    // Old
    // value={
    //   calculation.customCalculation
    //     ? calculation.customCalculation 
    //     : calculation.isFixed
    //       ? `${PriceFormat(calculation.value)}`
    //       : `${calculation.value}% of ${PriceFormat(calculation.baseValue)} (${PriceFormat(calculation.baseValue * calculation.value / 100)})`

    return (
      <div className="py-2 grid grid-cols-2 gap-4">
        <dt className="text-sm font-medium text-gray-500">
          {name}
        </dt>
        <dd className={`items-center text-sm ${isAddition ? '' : 'text-red-600'}`}>
          <div>
            {customCalculation ? output : `${isAddition ? "+" : "-"} ${output}`}
          </div>
        </dd>
      </div>
    )
  };

  const StatusRow = ({ label, value, classes }: { label: string, value: string, classes: string }) => (
    <div className="py-2 grid grid-cols-2 gap-4">
      <dt className="text-sm font-medium text-gray-500">
        {label}
      </dt>
      <dd className={`items-center text-sm ${classes}`}>
        <div>
          {value}
        </div>
      </dd>
    </div>
  );

  const ExtractCalculationData = (json: string) => {
    if (!json || json === "" || json === "{}") return null;
    let raw: any = null;
    try {
      raw = JSON.parse(json);
    } catch {
      return null;
    }
    if (!raw) return null;
    let data: ICalculationData = {
      salvageCalculations: raw.SalvageCalculations ?? [],
      tradePoorCalculations: raw.TradePoorCalculations ?? [],
      postcodeCalculations: raw.PostcodeCalculations ?? [],
      catalyticCalculations: raw.CatalyticCalculations ?? [],
      engineCalculations: raw.EngineCalculations ?? [],
      vfeCalculations: raw.VfeCalculations ?? [],
      generalCalculations: raw.GeneralCalculations ?? [],
      genericCalculations: raw.GenericCalculations ?? [],
      additionalCalculations: raw.AdditionalCalculations ?? [],
      partsLookupCalculations: raw.PartsLookupCalculations ?? [],
      partsLookupGenericCalculations: raw.PartsLookupGenericCalculations ?? [],
      dropOff: raw.DropOff ?? 0,
      payment: raw.Payment ?? {},
      salvageVoucherCalculation: raw.SalvageVoucherCalculation ?? {},
      scrapVoucherCalculation: raw.ScrapVoucherCalculation ?? {}
    };
    return data;
  };

  const ShouldIncludeCalculation = (calculation: ApiCalculation, leadIsCollection: boolean) => {
    if (typeof calculation?.DropOff !== "boolean") return true;
    return leadIsCollection ? !calculation.DropOff : calculation.DropOff;
  };

  const ToCalculation = (calculation: ApiCalculation): ICalculation | null => {
    if (!calculation?.Name) return null;
    if ((calculation.Value == null || Number.isNaN(calculation.Value)) && !calculation.CustomCalculation) return null;

    return {
      name: calculation.Name,
      isFixed: calculation.IsFixed ?? true,
      value: calculation.Value ?? 0,
      baseValue: calculation.BaseValue ?? 0,
      isAddition: calculation.IsAddition ?? true,
      customCalculation: calculation.CustomCalculation ?? ""
    };
  };

  const NormalizeApiCalculationArray = (data: unknown): ApiCalculation[] => {
    if (!Array.isArray(data)) return [];
    return data as ApiCalculation[];
  };

  const ExtractCalculations = (data: ApiCalculation[], leadIsCollection: boolean) => {
    const extractedCalculations: ICalculation[] = [];

    for (let i = 0; i < data.length; i++) {
      if (!ShouldIncludeCalculation(data[i], leadIsCollection)) continue;

      const calculation = ToCalculation(data[i]);
      if (!calculation) continue;

      extractedCalculations.push(calculation);
    }
    return extractedCalculations;
  };

  const BuildRowsForSide = (data: ICalculationData, currentQuoteType: QuoteType, currentIsCollection: boolean) => {
    const rows: ICalculation[] = [];

    switch (currentQuoteType) {
      case QuoteType.Salvage:
        rows.push(
          ...ExtractCalculations(NormalizeApiCalculationArray(data.tradePoorCalculations), currentIsCollection),
          ...ExtractCalculations(NormalizeApiCalculationArray(data.salvageCalculations), currentIsCollection),
          ...ExtractCalculations([data.salvageVoucherCalculation as unknown as ApiCalculation], currentIsCollection),
        );
        break;
      case QuoteType.ScrapExport:
        rows.push(
          ...ExtractCalculations(NormalizeApiCalculationArray(data.postcodeCalculations), currentIsCollection),
          ...ExtractCalculations(NormalizeApiCalculationArray(data.catalyticCalculations), currentIsCollection),
          ...ExtractCalculations(NormalizeApiCalculationArray(data.engineCalculations), currentIsCollection),
          ...ExtractCalculations(NormalizeApiCalculationArray(data.vfeCalculations), currentIsCollection),
          ...ExtractCalculations(NormalizeApiCalculationArray(data.generalCalculations), currentIsCollection),
          ...ExtractCalculations(NormalizeApiCalculationArray(data.genericCalculations), currentIsCollection),
          ...ExtractCalculations(NormalizeApiCalculationArray(data.additionalCalculations), currentIsCollection),
          ...ExtractCalculations([data.scrapVoucherCalculation as unknown as ApiCalculation], currentIsCollection),
        );
        break;
      case QuoteType.SmartPrice:
        rows.push(
          ...ExtractCalculations(NormalizeApiCalculationArray(data.partsLookupCalculations), currentIsCollection),
          ...ExtractCalculations(NormalizeApiCalculationArray(data.partsLookupGenericCalculations), currentIsCollection),
          ...ExtractCalculations([data.scrapVoucherCalculation as unknown as ApiCalculation], currentIsCollection),
        );
        break;
    }

    rows.push(
      ...ExtractCalculations([data.payment as unknown as ApiCalculation], currentIsCollection)
    );

    return rows;
  };

  useEffect(() => {
    if (!singleLead.isLoading && singleLead.data) {
      const leadData = singleLead.data.data;
      setLead(leadData);

      let data: ICalculationData | null = ExtractCalculationData(leadData.calculationJson!);

      if (!data) {
        setIsLoading(false);
        return;
      }

      const currentIsCollection = leadData.isCollection ?? isCollection;
      const currentQuoteType = leadData.quoteType ?? quoteType;
      const rowsForCurrentSide = BuildRowsForSide(data, currentQuoteType, currentIsCollection);
      setCalculations(rowsForCurrentSide);
      setDropOff(data.dropOff);
      setIsLoading(false);
    }
  }, [singleLead.isLoading, singleLead.data, isCollection, quoteType]);

  if (isLoading || lead == undefined) return <Card bodyClassName="p-4" title="Quote Calculation"><LoadingWheel /></Card>;

  return (
    <Card bodyClassName="p-4 divide-y divide-gray-200" collapsible
      title={`
                ${GetQuoteType(lead.quoteType)}
                ${lead.isCollection ? "Collection" : "Drop Off"} 
                Calculation
            `}>
      {calculations != null && calculations?.length > 0 && calculations?.map((calculation, index) => (
        <CalculationStatusRow
          key={index}
          calculation={calculation}
        />
      ))}
      {!lead.isCollection && dropOff &&
        <StatusRow label="Drop Off" value={PriceFormat(dropOff)} classes="text-gray-900" />
      }

      <hr />
      <StatusRow
        label="Total"
        value={PriceFormat(total)}
        classes="font-medium text-gray-900"
      />
    </Card>
  );
};